import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useCallback } from 'react';
import { Button, useToast, useTheme } from '@vertesia/ui/core';
import { useUserSession } from '@vertesia/ui/session';
import { useNavigate } from '@vertesia/ui/router';
import { MonacoEditor } from '@vertesia/ui/widgets';
import { useUITranslation } from '../../../../i18n/index.js';
import { SaveVersionConfirmModal } from './SaveVersionConfirmModal.js';
function getMonacoLanguage(contentType) {
    switch (contentType) {
        case 'text/markdown':
            return 'markdown';
        case 'application/json':
            return 'json';
        case 'application/xml':
        case 'text/xml':
            return 'xml';
        default:
            return 'plaintext';
    }
}
export function TextEditorPanel({ object, text, onClose, onSaved }) {
    const { store } = useUserSession();
    const toast = useToast();
    const { t } = useUITranslation();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const editorRef = useRef(undefined);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const language = getMonacoLanguage(object.content?.type);
    console.log('Determined language for Monaco Editor:', language);
    console.log('TextEditorPanel rendered with object:', object, text);
    const handleEditorChange = useCallback(() => {
        if (!isDirty)
            setIsDirty(true);
    }, [isDirty]);
    function handleSave() {
        if (!editorRef.current)
            return;
        setShowConfirmation(true);
    }
    async function saveText(createVersion, versionLabel) {
        if (!editorRef.current)
            return;
        const editorText = editorRef.current.getValue();
        const contentType = object.content?.type || 'text/plain';
        const fileName = object.content?.name || 'content.txt';
        try {
            setIsSaving(true);
            const blob = new Blob([editorText], { type: contentType });
            const file = new File([blob], fileName, { type: contentType });
            const response = await store.objects.update(object.id, {
                content: file,
            }, {
                createRevision: createVersion,
                revisionLabel: versionLabel,
                ifMatch: object.content?.etag,
            });
            toast({
                status: 'success',
                title: t('store.textSaved'),
                duration: 2000,
            });
            setShowConfirmation(false);
            if (createVersion && response.id !== object.id) {
                onClose();
                setTimeout(() => {
                    navigate(`/objects/${response.id}`);
                }, 100);
            }
            else {
                onSaved();
            }
        }
        catch (error) {
            const is412 = error?.status === 412 || error?.message?.includes('412');
            toast({
                status: 'error',
                title: t('store.errorSavingText'),
                description: is412
                    ? t('store.textConflict')
                    : (error.message || t('store.errorSavingTextDefault')),
                duration: 5000,
            });
        }
        finally {
            setIsSaving(false);
        }
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2 px-2 py-1 shrink-0", children: [isDirty && (_jsx("span", { className: "text-xs text-attention", children: t('store.unsavedChanges') })), _jsx("div", { className: "flex-1" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: onClose, disabled: isSaving, children: t('store.cancelEdit') }), _jsx(Button, { variant: "secondary", size: "sm", onClick: handleSave, disabled: !isDirty, isLoading: isSaving, children: t('store.saveText') })] }), _jsx("div", { className: "flex-1 min-h-0 border rounded-md overflow-hidden mx-2 mb-2", children: _jsx(MonacoEditor, { value: text, language: language, editorRef: editorRef, onChange: handleEditorChange, theme: theme === 'dark' ? 'vs-dark' : 'vs', options: {
                        wordWrap: 'on',
                        minimap: { enabled: false },
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                    } }) }), _jsx(SaveVersionConfirmModal, { isOpen: showConfirmation, onClose: () => setShowConfirmation(false), onConfirm: saveText, isLoading: isSaving })] }));
}
//# sourceMappingURL=TextEditorPanel.js.map